import { writable, type Writable } from 'svelte/store';

import type { OptionList } from '$saga/types/search.types';

import { Currency } from '../../utils/currency';
import { defaultFormLabels, defaultFormValues } from './constants';
import {
    FilterType,
    Sorting,
    type FilterFormLabels,
    type FilterFormValues,
} from './types';

export type SearchStoreState = {
    formLabels: FilterFormLabels;
    formValues: FilterFormValues;
    defaultDurationMin: number | undefined;
    defaultDurationMax: number | undefined;
    defaultBudgetMin: number | undefined;
    defaultBudgetMax: number | undefined;
    defaultLevels: OptionList | undefined;
    defaultTags: OptionList | undefined;
    defaultSkiResorts: OptionList | undefined;
    defaultTypes: OptionList | undefined;
    defaultDestinationOptions: OptionList | undefined;
    defaultSportOptions: OptionList | undefined;
    alpineSkiingSportId: string | null;
    currency: Currency;
    isHomePage: boolean;
    isSubmitting: boolean;
    isDirty: boolean;
    // Landing page context for redirect logic
    isLandingPage: boolean;
    landingPageType:
        | 'sport'
        | 'theme'
        | 'destination'
        | 'landing'
        | 'lastminute'
        | null;
    pillarFilters: {
        sports?: string[];
        destinations?: string[];
        tags?: string[];
        hasDateRange?: boolean; // For last minute page
    };
    hideVerticalBar: boolean; // For landing pages where we want to hide the vertical bar
};

export const filtersContext: Writable<SearchStoreState> = writable({
    formLabels: defaultFormLabels,
    formValues: defaultFormValues,
    defaultDurationMin: undefined,
    defaultDurationMax: undefined,
    defaultBudgetMin: undefined,
    defaultBudgetMax: undefined,
    defaultLevels: undefined,
    defaultTags: undefined,
    defaultSkiResorts: undefined,
    defaultTypes: undefined,
    defaultDestinationOptions: undefined,
    defaultSportOptions: undefined,
    alpineSkiingSportId: null,
    currency: Currency.EUR,
    isHomePage: false,
    isSubmitting: false,
    isDirty: false,
    isLandingPage: false,
    landingPageType: null,
    pillarFilters: {},
    hideVerticalBar: false,
});

export const filteredToursCount = writable<number | null>(null);

export const sortValue: Writable<string> = writable(Sorting.DEFAULT);

export const mainFiltersModalOpen = writable(false);
export const secondaryFiltersModalOpen = writable(false);
// Shared selected filter type for cross-component coordination (instead of local component variable)
export const mainFiltersSelectedFilterType = writable<FilterType>(
    FilterType.SELECTION,
);

let prevFormValues: FilterFormValues | null = null;
let isFirstRun = true;

filtersContext.subscribe(($ctx) => {
    if (isFirstRun) {
        isFirstRun = false;
        prevFormValues = JSON.parse(JSON.stringify($ctx.formValues));
        return;
    }

    const hasChanged =
        JSON.stringify($ctx.formValues) !== JSON.stringify(prevFormValues);

    if (hasChanged !== $ctx.isDirty) {
        filtersContext.update((ctx) => ({
            ...ctx,
            isDirty: hasChanged,
        }));
    }
});

export function setDurationFilter(
    min: number | null,
    max: number | null,
): void {
    filtersContext.update((context) => ({
        ...context,
        formValues: {
            ...context.formValues,
            durationMin: min === context.defaultDurationMin ? null : min,
            durationMax: max === context.defaultDurationMax ? null : max,
        },
    }));
}

export function setBudgetFilter(min: number | null, max: number | null): void {
    filtersContext.update((context) => ({
        ...context,
        formValues: {
            ...context.formValues,
            budgetMin: min === context.defaultBudgetMin ? null : min,
            budgetMax: max === context.defaultBudgetMax ? null : max,
        },
    }));
}

// create a derived store to determine if any filters are active
export const areSecondaryFiltersActive = writable(false);
export const areMainFiltersActive = writable(false);

filtersContext.subscribe(($context) => {
    const values = $context.formValues;

    const isSecondaryFiltersActive =
        values.levels.length > 0 ||
        values.tags.length > 0 ||
        values.resorts.length > 0 ||
        values.types.length > 0 ||
        values.budgetMin !== defaultFormValues.budgetMin ||
        values.budgetMax !== defaultFormValues.budgetMax ||
        values.months.length > 0 ||
        values.bestReviewed ||
        values.exclusiveSport;

    const isMainFiltersActive =
        values.theme.length > 0 ||
        values.destination.length > 0 ||
        values.nbPax !== defaultFormValues.nbPax ||
        values.startDate !== defaultFormValues.startDate ||
        values.endDate !== defaultFormValues.endDate ||
        values.months.length > 0;

    areSecondaryFiltersActive.set(isSecondaryFiltersActive);
    areMainFiltersActive.set(isMainFiltersActive);
});
